/**
 * TeamPerformanceTable Component
 * Sortable table of team member performance
 */

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import {
  User,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronUp,
  ChevronDown,
  Search,
} from 'lucide-react';
import { getScoreColor, getScoreCategory } from '../../../types';

export interface TeamMemberPerformance {
  userId: string;
  userName: string;
  userEmail?: string;
  avatarUrl?: string;
  score: number;
  previousScore?: number;
  okrProgress: number;
  checkInCompliance: number;
  engagement: number;
  okrCount: number;
  okrsCompleted: number;
}

export interface TeamPerformanceTableProps {
  members: TeamMemberPerformance[];
  title?: string;
  onMemberClick?: (userId: string) => void;
  className?: string;
  isLoading?: boolean;
}

type SortField = 'name' | 'score' | 'okrProgress' | 'checkInCompliance' | 'engagement';
type SortDirection = 'asc' | 'desc';

const CATEGORY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  needsImprovement: 'Needs Improvement',
  poor: 'Poor',
};

export const TeamPerformanceTable: React.FC<TeamPerformanceTableProps> = ({
  members,
  title = 'Team Performance',
  onMemberClick,
  className,
  isLoading = false,
}) => {
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAndFilteredMembers = useMemo(() => {
    let result = [...members];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.userName.toLowerCase().includes(query) ||
          m.userEmail?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'name':
          aVal = a.userName.toLowerCase();
          bVal = b.userName.toLowerCase();
          break;
        case 'score':
          aVal = a.score;
          bVal = b.score;
          break;
        case 'okrProgress':
          aVal = a.okrProgress;
          bVal = b.okrProgress;
          break;
        case 'checkInCompliance':
          aVal = a.checkInCompliance;
          bVal = b.checkInCompliance;
          break;
        case 'engagement':
          aVal = a.engagement;
          bVal = b.engagement;
          break;
        default:
          aVal = a.score;
          bVal = b.score;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [members, sortField, sortDirection, searchQuery]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-stone-300" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-blue-500" />
    ) : (
      <ChevronDown className="w-4 h-4 text-blue-500" />
    );
  };

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-36 mb-6" />
          <div className="h-10 bg-stone-200 rounded w-full mb-4" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 py-4 border-b border-stone-100">
              <div className="w-10 h-10 bg-stone-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-stone-200 rounded w-32 mb-2" />
                <div className="h-3 bg-stone-200 rounded w-24" />
              </div>
              <div className="h-6 bg-stone-200 rounded w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-8 text-stone-400">
          <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No team members found</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
        <span className="text-sm text-stone-500">{members.length} members</span>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          placeholder="Search team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th
                className="text-left py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Team Member
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-24"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center justify-center gap-1">
                  Score
                  <SortIcon field="score" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-24"
                onClick={() => handleSort('okrProgress')}
              >
                <div className="flex items-center justify-center gap-1">
                  OKR
                  <SortIcon field="okrProgress" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-24"
                onClick={() => handleSort('checkInCompliance')}
              >
                <div className="flex items-center justify-center gap-1">
                  Check-ins
                  <SortIcon field="checkInCompliance" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-24"
                onClick={() => handleSort('engagement')}
              >
                <div className="flex items-center justify-center gap-1">
                  Engagement
                  <SortIcon field="engagement" />
                </div>
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-24">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredMembers.map((member) => {
              const category = getScoreCategory(member.score);
              const trend =
                member.previousScore !== undefined
                  ? member.score - member.previousScore
                  : 0;

              return (
                <tr
                  key={member.userId}
                  className={clsx(
                    'border-b border-stone-100 last:border-b-0',
                    onMemberClick && 'hover:bg-stone-50 cursor-pointer'
                  )}
                  onClick={() => onMemberClick?.(member.userId)}
                >
                  {/* Name */}
                  <td className="py-4 px-2">
                    <div className="flex items-center gap-3">
                      {member.avatarUrl ? (
                        <img
                          src={member.avatarUrl}
                          alt={member.userName}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-stone-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-stone-800 truncate">
                          {member.userName}
                        </p>
                        {member.userEmail && (
                          <p className="text-xs text-stone-500 truncate">
                            {member.userEmail}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Score */}
                  <td className="py-4 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span
                        className="text-lg font-bold"
                        style={{ color: getScoreColor(member.score) }}
                      >
                        {Math.round(member.score)}
                      </span>
                      {trend > 0 && (
                        <TrendingUp className="w-3 h-3 text-green-500" />
                      )}
                      {trend < 0 && (
                        <TrendingDown className="w-3 h-3 text-red-500" />
                      )}
                      {trend === 0 && member.previousScore !== undefined && (
                        <Minus className="w-3 h-3 text-stone-400" />
                      )}
                    </div>
                  </td>

                  {/* OKR Progress */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className="text-sm font-medium"
                      style={{ color: getScoreColor(member.okrProgress) }}
                    >
                      {Math.round(member.okrProgress)}%
                    </span>
                  </td>

                  {/* Check-in Compliance */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className="text-sm font-medium"
                      style={{ color: getScoreColor(member.checkInCompliance) }}
                    >
                      {Math.round(member.checkInCompliance)}%
                    </span>
                  </td>

                  {/* Engagement */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className="text-sm font-medium"
                      style={{ color: getScoreColor(member.engagement) }}
                    >
                      {Math.round(member.engagement)}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 rounded text-xs font-medium',
                        category === 'excellent' && 'bg-green-100 text-green-700',
                        category === 'good' && 'bg-blue-100 text-blue-700',
                        category === 'needsImprovement' && 'bg-amber-100 text-amber-700',
                        category === 'poor' && 'bg-red-100 text-red-700'
                      )}
                    >
                      {CATEGORY_LABELS[category]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedAndFilteredMembers.length === 0 && searchQuery && (
        <div className="text-center py-8 text-stone-400">
          <p>No team members match "{searchQuery}"</p>
        </div>
      )}
    </Card>
  );
};

export default TeamPerformanceTable;
